import { Hide, useMobileMediaQuery } from '@iheartjane/reefer';
import isUndefined from 'lodash-es/isUndefined';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { useActiveFilterContext } from '../activeFilterProvider';
import { StyledFilterList } from '../filter.styles';
import { FilterButton } from '../filterButton';
import { FilterCheckbox } from '../filterCheckbox';
import type { FilterListProps } from './filterBarPopover.types';

export const FilterList = ({
  filterKey,
  filters,
  hideCounts,
  isNested,
  onChange,
  query,
}: FilterListProps) => {
  const { label, subItems, value } = filters;
  const [showSubItems, setShowSubItems] = useState(false);
  const hasSubItems = subItems && subItems.length > 0;
  const isMobile = useMobileMediaQuery({});
  const { activeFilters } = useActiveFilterContext();

  const isIndeterminate = subItems
    ?.map((subItem) =>
      activeFilters.some((filter) => filter.value === subItem.value)
    )
    .includes(true);

  const isTopLevelChecked =
    hasSubItems && activeFilters.some((filter) => filter.value === value);

  const subItemValues = hasSubItems
    ? subItems.map((subItem) => subItem.value)
    : undefined;

  /**
   * If the filter is for a bundle special, we hide the counts. Algola returns one count for qualifying
   * products and one for discountable products, but these can't simply be added together since it is
   * possible for a product to be in both groups and there is not a straightforward way to find the amount
   * of overlap.
   */
  const hideCountsForFilter =
    hideCounts || filterKey === 'applicable_bundle_special_ids';

  // multiselect filters can either be a checkbox or button,
  // depending on the screen size
  const renderedItem = (children?: ReactNode) => {
    // if there are no subItems, render as a button on mobile
    if (!children && !isNested && isMobile) {
      return (
        <FilterButton
          filterKey={filterKey}
          hideCounts={hideCountsForFilter}
          item={filters}
          onChange={onChange}
        />
      );
    }

    return (
      <FilterCheckbox
        key={label}
        filterKey={filterKey}
        hasSubItems={hasSubItems}
        hideCounts={hideCountsForFilter}
        indeterminate={isIndeterminate}
        item={filters}
        onChange={onChange}
        showSubItems={showSubItems}
        setShowSubItems={setShowSubItems}
        subItemValues={subItemValues}
        query={query}
      >
        {children}
      </FilterCheckbox>
    );
  };

  // for now, subItems are only rendered as checkboxes
  const renderedSubItems = subItems?.map((item) => {
    // if parent category is refined, fire onchange for parent category on sub item select
    return (
      <FilterCheckbox
        filterKey={filterKey}
        key={item.label}
        hideCounts={hideCountsForFilter}
        isTopLevelChecked={isTopLevelChecked}
        item={item}
        onChange={onChange}
        isSubItem={true}
        topLevelValue={value}
      />
    );
  });

  if (hasSubItems) {
    // passes subItems to be nested under their parent item in collapsible list
    return renderedItem(
      <Hide isHidden={!showSubItems} hideFromScreenReader>
        <StyledFilterList
          aria-label={`${label}: sub items`}
          query={isUndefined(query)}
          subList
        >
          {renderedSubItems}
        </StyledFilterList>
      </Hide>
    );
  }

  return renderedItem();
};
